// Copyright (c) 2020 embyt GmbH. All rights reserved.

import * as React from "react";
import type { JSX } from "react";
import { DayPicker, Matcher } from "react-day-picker";
import { FormattedMessage } from "react-intl";
import "react-day-picker/style.css";

import IconCalendar from "icons/iconCalendar";
import cssContent from "constants.module.scss";

import "./InlineDatePicker.scss";

// A native-like YYYY / MM / DD segmented input (each segment independently editable, with
// auto-advance and backspace-to-previous-segment) paired with a calendar-icon button that
// opens a self-contained calendar dropdown. Unlike a native <input type="date">, we fully
// control opening/closing/positioning of the dropdown, which sidesteps Safari's buggy native
// date-picker popover, while still allowing per-segment editing like the native control does.
//
// granularity controls both which segments are shown (year / year+month / year+month+day)
// and which picker is shown in the dropdown - "day" keeps the existing calendar, "month"
// and "year" show a grid of months/years instead (native <input type="month"> and
// type="number"/"year"-only inputs have inconsistent, or no, browser support).
interface IInlineDatePickerProps {
  id: string;
  value: string;
  selectedDate: Date | undefined;
  granularity?: "day" | "month" | "year";
  minDate?: Date;
  maxDate?: Date;
  hasError?: boolean;
  title?: string;
  onTextChange: (value: string) => void;
  onSelectDate: (date: Date) => void;
  onClear?: () => void;
}

interface IInlineDatePickerState {
  isOpen: boolean;
  openUpward: boolean;
}

// value is always either "", or "<year>[-<month>[-<day>]]" with each part being whatever
// digits (0-4 for year, 0-2 for month/day) have been typed so far, trimmed to the segments
// that granularity actually renders - never anything else, so it can always be split back
// apart unambiguously.
function splitValue(value: string): { year: string; month: string; day: string } {
  const [year = "", month = "", day = ""] = value.split("-");
  return { year, month, day };
}

function composeValue(year: string, month: string, day: string): string {
  if (year === "" && month === "" && day === "") {
    return "";
  }
  if (month === "" && day === "") {
    return year;
  }
  if (day === "") {
    return `${year}-${month}`;
  }
  return `${year}-${month}-${day}`;
}

// Compares calendar days only, ignoring time-of-day - two `new Date()` calls taken moments
// apart (e.g. one in the parent's render, one here) would otherwise never compare equal.
function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

function isMonthInRange(year: number, month: number, minDate?: Date, maxDate?: Date): boolean {
  const monthStart = new Date(year, month, 1);
  const monthEnd = new Date(year, month + 1, 1);
  if (minDate && monthEnd <= minDate) return false;
  if (maxDate && monthStart > maxDate) return false;
  return true;
}

function isYearInRange(year: number, minDate?: Date, maxDate?: Date): boolean {
  const yearStart = new Date(year, 0, 1);
  const yearEnd = new Date(year + 1, 0, 1);
  if (minDate && yearEnd <= minDate) return false;
  if (maxDate && yearStart > maxDate) return false;
  return true;
}

interface IGridPickerProps {
  selected: Date | undefined;
  minDate?: Date;
  maxDate?: Date;
  onSelect: (date: Date) => void;
}

// grid-style month picker shown in the dropdown when granularity === "month" - a plain
// day-calendar has no notion of "select a whole month", so this shows the 12 months of one
// year at a time instead, with year-at-a-time navigation.
function MonthGridPicker(props: IGridPickerProps) {
  const initialYear = (props.selected ?? props.maxDate ?? new Date()).getFullYear();
  const [displayYear, setDisplayYear] = React.useState(initialYear);

  const monthLabels = React.useMemo(
    () =>
      Array.from({ length: 12 }, (_, monthIndex) =>
        new Date(2000, monthIndex, 1).toLocaleString(undefined, { month: "short" }),
      ),
    [],
  );

  return (
    <div className="grid-picker">
      <div className="grid-picker-header">
        <button
          type="button"
          className="grid-picker-nav"
          aria-label="Previous year"
          onClick={() => setDisplayYear((year) => year - 1)}
        >
          ‹
        </button>
        <span className="grid-picker-title">{displayYear}</span>
        <button
          type="button"
          className="grid-picker-nav"
          aria-label="Next year"
          onClick={() => setDisplayYear((year) => year + 1)}
        >
          ›
        </button>
      </div>
      <div className="grid-picker-cells">
        {monthLabels.map((label, monthIndex) => {
          const isSelected =
            props.selected?.getFullYear() === displayYear &&
            props.selected?.getMonth() === monthIndex;
          return (
            <button
              type="button"
              key={label}
              className={`grid-picker-cell ${isSelected ? "selected" : ""}`}
              disabled={!isMonthInRange(displayYear, monthIndex, props.minDate, props.maxDate)}
              onClick={() => props.onSelect(new Date(displayYear, monthIndex, 1))}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const YEAR_BLOCK_SIZE = 12;

// grid-style year picker shown in the dropdown when granularity === "year", navigating a
// block of years (rather than one at a time) since a single year rarely needs fine-grained
// navigation.
function YearGridPicker(props: IGridPickerProps) {
  const initialYear = (props.selected ?? props.maxDate ?? new Date()).getFullYear();
  const [blockStart, setBlockStart] = React.useState(initialYear - (initialYear % YEAR_BLOCK_SIZE));
  const years = Array.from({ length: YEAR_BLOCK_SIZE }, (_, index) => blockStart + index);

  return (
    <div className="grid-picker">
      <div className="grid-picker-header">
        <button
          type="button"
          className="grid-picker-nav"
          aria-label="Previous years"
          onClick={() => setBlockStart((start) => start - YEAR_BLOCK_SIZE)}
        >
          ‹
        </button>
        <span className="grid-picker-title">
          {blockStart} – {blockStart + YEAR_BLOCK_SIZE - 1}
        </span>
        <button
          type="button"
          className="grid-picker-nav"
          aria-label="Next years"
          onClick={() => setBlockStart((start) => start + YEAR_BLOCK_SIZE)}
        >
          ›
        </button>
      </div>
      <div className="grid-picker-cells">
        {years.map((year) => (
          <button
            type="button"
            key={year}
            className={`grid-picker-cell ${props.selected?.getFullYear() === year ? "selected" : ""}`}
            disabled={!isYearInRange(year, props.minDate, props.maxDate)}
            onClick={() => props.onSelect(new Date(year, 0, 1))}
          >
            {year}
          </button>
        ))}
      </div>
    </div>
  );
}

export default class InlineDatePicker extends React.Component<
  IInlineDatePickerProps,
  IInlineDatePickerState
> {
  state: IInlineDatePickerState = { isOpen: false, openUpward: false };

  private yearRef = React.createRef<HTMLInputElement>();
  private monthRef = React.createRef<HTMLInputElement>();
  private dayRef = React.createRef<HTMLInputElement>();
  private triggerRef = React.createRef<HTMLButtonElement>();

  // Decided once, when opening, from the trigger's position - rather than measuring the
  // popover itself, which isn't in the DOM yet at that point. The estimate is generous
  // (compact calendar + footer), so it only flips upward when there's clearly not enough
  // room below, and only if there's more room above than below.
  private shouldOpenUpward(): boolean {
    const rect = this.triggerRef.current?.getBoundingClientRect();
    if (!rect) return false;

    const estimatedMenuHeight = 360;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    return spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow;
  }

  private Blanket = (props: JSX.IntrinsicElements["div"]) => (
    <div
      style={{
        bottom: 0,
        left: 0,
        top: 0,
        right: 0,
        position: "fixed",
        zIndex: cssContent.DROPDOWN_BUTTON_Z_INDEX,
      }}
      {...props}
    />
  );

  private Menu = (props: JSX.IntrinsicElements["div"]) => (
    <div
      className="date-picker-popover shadow"
      style={{
        position: "absolute",
        ...(this.state.openUpward ? { bottom: "calc(100% + 4px)" } : { top: "calc(100% + 4px)" }),
        left: 0,
        zIndex: cssContent.DROPDOWN_FOREGROUND_Z_INDEX,
      }}
      {...props}
    />
  );

  // moves focus to the previous segment (used on Backspace out of an empty segment, and on
  // ArrowLeft at the start of a segment)
  private focusPrevious(ref: React.RefObject<HTMLInputElement | null>) {
    ref.current?.focus();
    ref.current?.select();
  }

  private selectDate(date: Date) {
    this.setState({ isOpen: false });
    this.props.onSelectDate(date);
  }

  public render() {
    const granularity = this.props.granularity ?? "day";
    const showMonthSegment = granularity !== "year";
    const showDaySegment = granularity === "day";

    const { year, month, day } = splitValue(this.props.value);

    const disabledMatchers: Matcher[] = [
      ...(this.props.minDate ? [{ before: this.props.minDate }] : []),
      ...(this.props.maxDate ? [{ after: this.props.maxDate }] : []),
    ];

    const today = new Date();
    const todayStart = startOfDay(today);
    const isTodayDisabled =
      (this.props.minDate !== undefined && todayStart < startOfDay(this.props.minDate)) ||
      (this.props.maxDate !== undefined && todayStart > startOfDay(this.props.maxDate));
    const isClearDisabled = !this.props.selectedDate && this.props.value === "";

    return (
      <div className="date-input-group">
        <div
          id={this.props.id}
          className={`dateinput date-segments ${this.props.hasError ? "error" : ""}`}
          title={this.props.title}
        >
          <input
            ref={this.yearRef}
            className="date-segment date-segment-year"
            aria-label="Year"
            type="text"
            inputMode="numeric"
            maxLength={4}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            placeholder="YYYY"
            value={year}
            onFocus={(event) => event.currentTarget.select()}
            onChange={(event) => {
              const digits = event.currentTarget.value.replace(/[^0-9]/g, "").slice(0, 4);
              this.props.onTextChange(composeValue(digits, month, day));
              if (digits.length === 4) {
                this.monthRef.current?.focus();
                this.monthRef.current?.select();
              }
            }}
            onBlur={(event) => {
              // unlike month/day, a partial year can't be sensibly zero-padded into a real
              // year - so rather than silently keeping e.g. a single leftover digit as if it
              // were accepted, clear it back out once focus leaves
              const digits = event.currentTarget.value;
              if (digits.length > 0 && digits.length < 4) {
                this.props.onTextChange(composeValue("", month, day));
              }
            }}
          />
          {showMonthSegment && <span className="date-segment-separator">-</span>}
          {showMonthSegment && (
            <input
              ref={this.monthRef}
              className="date-segment date-segment-month"
              aria-label="Month"
              type="text"
              inputMode="numeric"
              maxLength={2}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              placeholder="MM"
              value={month}
              onFocus={(event) => event.currentTarget.select()}
              onKeyDown={(event) => {
                if (event.key === "Backspace" && event.currentTarget.value === "") {
                  this.focusPrevious(this.yearRef);
                }
              }}
              onChange={(event) => {
                const digits = event.currentTarget.value.replace(/[^0-9]/g, "").slice(0, 2);
                this.props.onTextChange(composeValue(year, digits, day));
                if (digits.length === 2) {
                  this.dayRef.current?.focus();
                  this.dayRef.current?.select();
                }
              }}
              onBlur={(event) => {
                const digits = event.currentTarget.value;
                if (digits.length === 1) {
                  this.props.onTextChange(composeValue(year, digits.padStart(2, "0"), day));
                }
              }}
            />
          )}
          {showDaySegment && <span className="date-segment-separator">-</span>}
          {showDaySegment && (
            <input
              ref={this.dayRef}
              className="date-segment date-segment-day"
              aria-label="Day"
              type="text"
              inputMode="numeric"
              maxLength={2}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              placeholder="DD"
              value={day}
              onFocus={(event) => event.currentTarget.select()}
              onKeyDown={(event) => {
                if (event.key === "Backspace" && event.currentTarget.value === "") {
                  this.focusPrevious(this.monthRef);
                }
              }}
              onChange={(event) => {
                const digits = event.currentTarget.value.replace(/[^0-9]/g, "").slice(0, 2);
                this.props.onTextChange(composeValue(year, month, digits));
              }}
              onBlur={(event) => {
                const digits = event.currentTarget.value;
                if (digits.length === 1) {
                  this.props.onTextChange(composeValue(year, month, digits.padStart(2, "0")));
                }
              }}
            />
          )}
        </div>
        <button
          ref={this.triggerRef}
          type="button"
          className="date-picker-trigger img-link"
          aria-label="Open calendar"
          onClick={() =>
            this.setState(({ isOpen, openUpward }) =>
              isOpen ?
                { isOpen: false, openUpward }
              : { isOpen: true, openUpward: this.shouldOpenUpward() },
            )
          }
        >
          <IconCalendar />
        </button>
        {this.state.isOpen && (
          <>
            <this.Menu>
              {granularity === "day" && (
                <DayPicker
                  mode="single"
                  captionLayout="dropdown"
                  startMonth={this.props.minDate}
                  endMonth={this.props.maxDate}
                  selected={this.props.selectedDate}
                  defaultMonth={this.props.selectedDate ?? this.props.maxDate ?? new Date()}
                  disabled={disabledMatchers}
                  onSelect={(date) => {
                    if (date) {
                      this.selectDate(date);
                    } else {
                      this.setState({ isOpen: false });
                    }
                  }}
                />
              )}
              {granularity === "month" && (
                <MonthGridPicker
                  selected={this.props.selectedDate}
                  minDate={this.props.minDate}
                  maxDate={this.props.maxDate}
                  onSelect={(date) => this.selectDate(date)}
                />
              )}
              {granularity === "year" && (
                <YearGridPicker
                  selected={this.props.selectedDate}
                  minDate={this.props.minDate}
                  maxDate={this.props.maxDate}
                  onSelect={(date) => this.selectDate(date)}
                />
              )}
              {granularity === "day" && (
                <div className="date-picker-footer">
                  <button
                    type="button"
                    className="date-picker-footer-btn"
                    disabled={isTodayDisabled}
                    onClick={() => this.selectDate(today)}
                  >
                    <FormattedMessage id="Today" />
                  </button>
                  <button
                    type="button"
                    className="date-picker-footer-btn"
                    disabled={isClearDisabled}
                    onClick={() => {
                      this.setState({ isOpen: false });
                      this.props.onClear?.();
                    }}
                  >
                    <FormattedMessage id="Clear" />
                  </button>
                </div>
              )}
            </this.Menu>
            <this.Blanket onClick={() => this.setState({ isOpen: false })} />
          </>
        )}
      </div>
    );
  }
}
